import {Button, Card, Col, message, Row, Space, Spin, Table,} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMoneyBillTrendUp, faSackDollar, faUser, faUserTie} from "@fortawesome/free-solid-svg-icons";
import {useEffect, useState} from "react";
import {Link, useHistory} from "react-router-dom";
import {EyeOutlined} from "@ant-design/icons";
import adminService from "../../service/adminService";
import {MSG_FETCH_MANAGER_SUMMARY_ERROR} from "../../util/Messages";
import ErrorHandler from "../../util/ErrorHandler";
import {ManagerSummaryDto} from "../../dto/admin/ManagerSummaryDto";
import CommonFunction from "../../util/CommonFunction";
import {RelationshipManagerSummaryDto} from "../../dto/admin/RelationshipManagerSummaryDto";
import TableSearchComp from "../../components/common/tableSearch/TableSearchComp";

const ManagerDashboard = () => {
        const [managerSummary, setManagerSummary] = useState<ManagerSummaryDto>();
        const [relationshipManagerList, setRelationshipManagerList] = useState<RelationshipManagerSummaryDto[]>();
        const [isSummaryLoading, setSummaryLoading] = useState(false);
        const [isTableLoading, setTableLoading] = useState(false);
        const [loggedManagerCode, setLoggedManagerCode] = useState<string | null>(null);
        const history = useHistory();

        const handleViewClick = (rmCode: string) => {
            localStorage.setItem("rmCode", rmCode)
            history.push(`/workflow/admin/relationship-manager-dashboard`)
        };
        const columns = [
            {
                title: 'Name of RM',
                dataIndex: 'rmName',
                key: 'rmName',
                sorter: (a: any, b: any) => {
                    a = a.rmName == null ? '' : a.rmName
                    b = b.rmName == null ? '' : b.rmName
                    return a.localeCompare(b);
                },
                ...TableSearchComp('rmName', 'Name of RM'),
                render: (text: any) => (
                    CommonFunction.validateText(text)
                ),
            },
            {
                title: 'AUM',
                dataIndex: 'aum',
                key: 'aum',
                align: "right",
                sorter: (a: any, b: any) => (a.aum - b.aum),
                ...TableSearchComp('aum', 'AUM'),
                render: (text: any) => (
                    CommonFunction.decimalFormat(text)
                ),
            },
            {
                title: 'Y2D Net Cash Flow',
                dataIndex: 't2DCashFlow',
                key: 't2DCashFlow',
                align: "right",
                sorter: (a: any, b: any) => (a.t2DCashFlow - b.t2DCashFlow),
                ...TableSearchComp('t2DCashFlow', 'Y2D Net Cash Flow'),
                render: (text: any) => (
                    CommonFunction.decimalFormat(text)
                ),
            },
            {
                title: 'Y2D New Accounts',
                dataIndex: 't2DNewAccounts',
                key: 't2DNewAccounts',
                align: "right",
                sorter: (a: any, b: any) => (a.t2DNewAccounts - b.t2DNewAccounts),
                ...TableSearchComp('t2DNewAccounts', 'Y2D New Accounts'),
                render: (text: any) => (
                    CommonFunction.addThousandSeparators(text)
                ),
            },
            {
                title: 'Action',
                key: 'action',
                align: "center",
                fixed: 'right' as const,
                render: (rec: RelationshipManagerSummaryDto) => (
                    <Space size="middle">
                        <Button className='btn-action-outline-common btn-action-view '
                                onClick={() => handleViewClick(rec.rmCode)}
                                icon={<EyeOutlined/>}>View</Button>
                    </Space>
                ),
            },
        ];


        useEffect(() => {
            const managerCode = localStorage.getItem("managerCode");
            setLoggedManagerCode(managerCode)
            fetchManagerSummary(managerCode)
            fetchRelationshipManagerList(managerCode)
        }, [])

        const fetchManagerSummary = (managerCode: string | null) => {
            if (null != managerCode) {
                setSummaryLoading(true)
                adminService.fetchManagerSummery(managerCode).then(res => {
                    if (res.data !== null) {
                        setManagerSummary(res.data.results)
                        setSummaryLoading(false)
                    } else {
                        message.error(MSG_FETCH_MANAGER_SUMMARY_ERROR).then();
                        setSummaryLoading(false)

                    }
                }, (reason) => {
                    ErrorHandler.handleError(reason);
                    setSummaryLoading(false)

                })
            }
        }

        const fetchRelationshipManagerList = (managerCode: string | null) => {
            if (null != managerCode) {
                setTableLoading(true)
                adminService.fetchRelationshipManagerList(managerCode).then(res => {
                    if (res.data !== null) {
                        setRelationshipManagerList(res.data.results)
                        setTableLoading(false)
                    } else {
                        message.error(MSG_FETCH_MANAGER_SUMMARY_ERROR).then();
                        setTableLoading(false)

                    }
                }, (reason) => {
                    ErrorHandler.handleError(reason);
                    setTableLoading(false)

                })
            }
        }


        return (
            <div className={'manager-dashboard-tbl-search'}>
                <Row gutter={[16, 16]}>
                    <Col span={24} className={'main-card-bg-none'}>
                        <Spin size='large' spinning={isSummaryLoading}>
                            <Card className={'total-wealth-card'}>
                                <Row gutter={[16, 16]} style={{marginTop: 6, marginBottom: 6}}>
                                    <Col span={6} className={'total-wealth-card-border-right'}>
                                        <div className="dashboard-info-card">
                                            <div className="card-icon card-1-icon">
                                                <FontAwesomeIcon icon={faUserTie}/>
                                            </div>
                                            <div className="card-info">
                                                <h5>Total Relationship Managers</h5>
                                                <h4>
                                                    {CommonFunction.addThousandSeparators(managerSummary?.numberOfRM)}
                                                </h4>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={6} className={'total-wealth-card-border-right'}>
                                        <div className="dashboard-info-card">
                                            <div className="card-icon card-2-icon">
                                                <FontAwesomeIcon icon={faSackDollar}/>
                                            </div>
                                            <div className="card-info">
                                                <h5>AUM</h5>
                                                <h4><span
                                                    className="lkr-span">LKR</span> {null == managerSummary?.aum ? "0.00" : CommonFunction.decimalFormat(String(managerSummary?.aum))}
                                                </h4>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={6} className={'total-wealth-card-border-right'}>
                                        <div className="dashboard-info-card">
                                            <div className="card-icon card-3-icon">
                                                <FontAwesomeIcon icon={faMoneyBillTrendUp}/>
                                            </div>
                                            <div className="card-info">
                                                <h5>Y2D Net Cash Flow</h5>
                                                <h4><span
                                                    className="lkr-span">LKR</span> {null == managerSummary?.t2DCashFlow ? "0.00" : CommonFunction.decimalFormat(String(managerSummary?.t2DCashFlow))}
                                                </h4>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div className="dashboard-info-card">
                                            <div className="card-icon card-4-icon">
                                                <FontAwesomeIcon icon={faUser}/>
                                            </div>
                                            <div className="card-info">
                                                <h5>Y2D New Accounts</h5>
                                                <h4>{CommonFunction.addThousandSeparators(managerSummary?.t2DNewAccounts)}</h4>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        </Spin>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{marginTop: 32}}>
                    <Col span={24}>
                        <Card className={'tab-card-custom-padding dashboard-card-box-shadow'}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={24} md={24} lg={24} xl={24} className='page-header-main-title-col'>
                                    <Col xs={24} sm={24} md={24} lg={24} xl={24} className='page-header-main-title-col'
                                         style={{paddingLeft: 0}}>
                                        <h2 className='page-header-main-title'>
                                            <div className='header_icon header-icon-center'>
                                                <FontAwesomeIcon icon={faUserTie} />
                                            </div>
                                             List of Relationship Managers
                                        </h2>
                                    </Col>
                                </Col>

                                <Col span={24}>
                                    <Table loading={isTableLoading} dataSource={relationshipManagerList} columns={columns}/>
                                </Col>
                            </Row>

                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
;

export default ManagerDashboard;